/**
 * Home's "Switch project" block (LED-1151) — All projects only; a scoped Home
 * has no page-level picker (the "‹ All projects" pill and the nav picker are
 * the way out).
 *
 * A row of big tappable tiles: the dark "All projects" tile, then the three
 * most recently walked projects. The project tiles carry NO photo — each has
 * a small MapThumb strip of its site with a ~10px ink pin in a white ring
 * (not the map's own big built-in pin) — then the last-walk date, the review
 * count, the integration mark and the name. On the right of the heading sits
 * the compact, fully rounded "Find a project" search, which is how a reader
 * reaches the other 100+.
 */

import { FONT_MONO, H } from "../../tokens";
import type { ProjectCardRow } from "../../api";
import { SiteMap } from "../../components/SiteMap";
import { PROCORE_MARK_URL } from "../../assets/procoreMark";
import { ProjectSearch, ICON } from "../../shell/ProjectPicker";
import type { PickerProject } from "../../shell/projectPins";

function monthDay(iso?: string): string {
  if (!iso) return "NO WALKS YET";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
}

export function HomeProjects({
  projects,
  recent,
  walkCounts,
  activeSiteId,
  hrefFor,
  onPick,
}: {
  /** Every project, for the search. */
  projects: PickerProject[];
  /** The three tiles, newest walk first. */
  recent: ProjectCardRow[];
  /** Walks per project, when known — the tile's "N walks" line. */
  walkCounts: Map<string, number>;
  activeSiteId: string | null;
  hrefFor: (siteId: string | null) => string;
  onPick: (siteId: string | null) => void;
}) {
  const tile: React.CSSProperties = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 156,
    padding: 12,
    borderRadius: 12,
    cursor: "pointer",
    overflow: "hidden",
    minWidth: 0,
    textAlign: "left",
  };
  return (
    <div data-testid="home-projects" style={{ marginTop: 34, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.3px" }}>Switch project</span>
          <span data-testid="home-projects-count" style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "1.3px", color: H.faint, whiteSpace: "nowrap" }}>
            {`${projects.length} ${projects.length === 1 ? "PROJECT" : "PROJECTS"}`}
          </span>
        </span>
        <ProjectSearch
          projects={projects}
          activeSiteId={activeSiteId}
          onSelect={(siteId) => onPick(siteId)}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
        <a
          href={hrefFor(null)}
          className="kb press"
          data-testid="project-tile-all"
          aria-current={!activeSiteId ? "true" : undefined}
          onClick={(event) => {
            event.preventDefault();
            onPick(null);
          }}
          style={{ ...tile, background: H.ink, border: `1px solid ${H.ink}`, color: H.white }}
        >
          <span style={{ width: 26, height: 26, borderRadius: 7, background: H.onInkWell, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={H.white} strokeWidth="2" strokeLinecap="round" aria-hidden>
              {ICON.list.split("|").map((d) => <path key={d} d={d} />)}
            </svg>
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.25px" }}>All projects</div>
            <div style={{ fontSize: 11.5, color: H.onInkSub, marginTop: 2 }}>
              {`${projects.length} ${projects.length === 1 ? "site" : "sites"}`}
            </div>
          </div>
        </a>
        {recent.map((project) => {
          const walks = walkCounts.get(project.id);
          return (
            <a
              key={project.id}
              href={hrefFor(project.id)}
              className="kb hcard press"
              data-testid="project-tile"
              data-site-id={project.id}
              onClick={(event) => {
                event.preventDefault();
                onPick(project.id);
              }}
              style={{ ...tile, minHeight: 92, background: H.white, color: H.ink }}
            >
              {/* The site strip: map only, and a small ringed ink pin. */}
              <div style={{ position: "relative", height: 64, margin: "-12px -12px 10px", overflow: "hidden", background: H.mapStrip, borderBottom: `1px solid ${H.hairFaint}` }}>
                <SiteMap id={project.id} location={project.location} address={project.address} size={260} />
                <span
                  data-testid="project-tile-pin"
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 10,
                    height: 10,
                    margin: "-5px 0 0 -5px",
                    borderRadius: 5,
                    background: H.ink,
                    border: `2px solid ${H.white}`,
                    boxShadow: `0 1px 3px ${H.pinShadow}`,
                    boxSizing: "border-box",
                    display: "block",
                  }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "1.1px", color: H.muted }}>
                  {monthDay(project.lastWalkAt)}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  {project.toReview > 0 && (
                    <span title={`${project.toReview} to review`} style={{ fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "0.8px", color: H.amberInk, background: H.amberSoft, padding: "2px 5px", borderRadius: 4 }}>
                      {project.toReview}
                    </span>
                  )}
                  {project.syncSource === "procore" && (
                    <img src={PROCORE_MARK_URL} alt="Procore" width={13} height={13} style={{ borderRadius: 3, display: "block" }} />
                  )}
                  {project.syncSource === "autodesk" && (
                    <span title="Autodesk" style={{ fontFamily: FONT_MONO, fontSize: 8, fontWeight: 700, color: H.sub }}>ACC</span>
                  )}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.25px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {project.name || project.id}
                </div>
                <div style={{ fontSize: 11.5, color: H.muted, marginTop: 2 }}>
                  {walks === undefined
                    ? project.lastWalkAt
                      ? "Walked"
                      : "No walks yet"
                    : `${walks} ${walks === 1 ? "walk" : "walks"}`}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
